import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, FileText, BookOpen, Lightbulb, Target } from 'lucide-react';

interface TermValidationIssue {
  term: string;
  suggestions: { canonical: string }[];
}

interface TermValidation {
  issues: TermValidationIssue[];
  suggestions?: unknown[];
}

interface ContextAnalysis {
  domain: string;
  intent: string;
  audience: string;
  confidence?: number;
}

interface CanonAlignment {
  matchedTerms: string[];
  missingTerms: string[];
}

interface LlmAnalysis {
  consistencyScore: number;
  canonAlignment?: CanonAlignment;
}

interface SemanticTerm {
  term: string;
  type: string;
}

interface UsageAnalysisEntry {
  term: string;
  type: string;
  isCorrectlyUsed: { valid: boolean; reason?: string };
  suggestions?: string[];
}

interface Contradiction {
  description: string;
  suggestion: string;
  severity: string;
}

interface Suggestion {
  suggestion?: string;
  reason?: string;
  reasoning?: string;
  priority?: string;
}

interface GradingResult {
  error?: string;
  message?: string;
  overallScore: number;
  termValidation?: TermValidation;
  contextAnalysis?: ContextAnalysis;
  llmEnabled?: boolean;
  llmAnalysis?: LlmAnalysis | null;
  semanticTerms?: SemanticTerm[];
  semanticScore?: number;
  usageAnalysis?: UsageAnalysisEntry[];
  contradictions?: Contradiction[];
  suggestions?: Suggestion[];
}

export const ConsistencyDashboard: React.FC = () => {
  const [textToGrade, setTextToGrade] = useState('');
  const [gradingResult, setGradingResult] = useState<GradingResult | null>(null);
  const [isGrading, setIsGrading] = useState(false);
  const [enableLLM, setEnableLLM] = useState(false);

  const handleGradeText = async () => {
    if (!textToGrade.trim()) return;
    
    setIsGrading(true);
    try {
      // Use the server-side API endpoint
      const response = await fetch('/api/consistency/grade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToGrade, enableLLM })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      setGradingResult(result);
    } catch (error) {
      console.error('Grading failed:', error);
      setGradingResult({
        error: 'Grading failed',
        message: error.message,
        termValidation: { issues: [], suggestions: [] },
        contextAnalysis: { domain: 'unknown', intent: 'unknown', audience: 'unknown', confidence: 0 },
        suggestions: [],
        overallScore: 0,
        llmAnalysis: null,
        llmEnabled: false
      });
    } finally {
      setIsGrading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-[var(--green)] dark:text-[var(--text-ter)]';
    if (score >= 0.6) return 'text-[var(--mustard)]';
    return 'text-[var(--coral)]';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 0.8) return 'Excellent';
    if (score >= 0.6) return 'Good';
    if (score >= 0.4) return 'Needs Work';
    return 'Poor';
  };

  return (
    <div className="min-h-screen bg-[var(--surface)] py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">Content Consistency Checker</h1>
          <p className="mt-2 text-[var(--text-sec)]">
            Validate your content against Canon sources and get actionable feedback
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Analysis */}
          <div className="lg:col-span-2 space-y-6">
            {/* Text Input */}
            <div className="bg-[var(--bg-raised)] shadow">
              <div className="px-6 py-4 border-b border-[var(--rule)]">
                <h2 className="text-lg font-semibold text-[var(--text-pri)]">Analyze Your Content</h2>
                <p className="text-sm text-[var(--text-sec)]">Paste text to check against Canon sources</p>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="text-input" className="block text-sm font-medium text-[var(--text-sec)]">
                      Content to analyze
                    </label>
                    <textarea
                      id="text-input"
                      rows={8}
                      className="mt-1 block w-full border-[var(--rule)] shadow-sm focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
                      placeholder="Paste your content here to check for consistency with Canon sources..."
                      value={textToGrade}
                      onChange={(e) => setTextToGrade(e.target.value)}
                    />
                  </div>
                  
                  {/* LLM Toggle */}
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={enableLLM}
                        onChange={(e) => setEnableLLM(e.target.checked)}
                        className="rounded border-[var(--rule)] text-consulting-slate-blue-600 focus:ring-consulting-slate-blue-500"
                      />
                      <span className="ml-2 text-sm text-[var(--text-sec)]">
                        Enable LLM Analysis (Advanced)
                      </span>
                    </label>
                  </div>
                  
                  <button
                    onClick={handleGradeText}
                    disabled={!textToGrade.trim() || isGrading}
                    className="w-full bg-consulting-slate-blue-600 text-white py-3 px-4 hover:bg-consulting-slate-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {isGrading ? (enableLLM ? 'AI Analyzing...' : 'Analyzing...') : 'Analyze Content'}
                  </button>
                </div>
              </div>
            </div>

            {/* Analysis Results */}
            {gradingResult && (
              <div className="bg-[var(--bg-raised)] shadow">
                <div className="px-6 py-4 border-b border-[var(--rule)]">
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">Analysis Results</h2>
                </div>
                <div className="p-6">
                  {/* Overall Score */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[var(--text-sec)]">Overall Score</span>
                      <span className={`text-lg font-bold ${getScoreColor(gradingResult.overallScore)}`}>
                        {Math.round(gradingResult.overallScore * 100)}% - {getScoreLabel(gradingResult.overallScore)}
                      </span>
                    </div>
                    <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${gradingResult.overallScore >= 0.8 ? 'bg-[var(--green)]' : gradingResult.overallScore >= 0.6 ? 'bg-[var(--mustard)]' : 'bg-[var(--coral)]'}`}
                        style={{ width: `${gradingResult.overallScore * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Term Validation */}
                  {gradingResult.termValidation && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3">Term Validation</h3>
                      {gradingResult.termValidation.issues.length > 0 ? (
                        <div className="space-y-2">
                          {gradingResult.termValidation.issues.map((issue, index) => (
                            <div key={index} className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                              <div className="flex items-start">
                                <AlertTriangle className="h-4 w-4 text-[var(--mustard)] mt-0.5 mr-2" />
                                <div>
                                  <p className="text-sm text-[var(--mustard)]">
                                    <strong>"{issue.term}"</strong> might be close to: {issue.suggestions.map(s => s.canonical).join(', ')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center text-[var(--green)] dark:text-[var(--text-ter)]">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          <span className="text-sm">All terms match Canon sources</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Context Analysis */}
                  {gradingResult.contextAnalysis && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3">Context Analysis</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-[var(--text-sec)]">Domain:</span>
                          <span className="ml-2 font-medium capitalize">{gradingResult.contextAnalysis.domain}</span>
                        </div>
                        <div>
                          <span className="text-[var(--text-sec)]">Intent:</span>
                          <span className="ml-2 font-medium capitalize">{gradingResult.contextAnalysis.intent}</span>
                        </div>
                        <div>
                          <span className="text-[var(--text-sec)]">Audience:</span>
                          <span className="ml-2 font-medium capitalize">{gradingResult.contextAnalysis.audience}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* LLM Analysis */}
                  {gradingResult.llmEnabled && gradingResult.llmAnalysis && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3 flex items-center">
                        <span className="mr-2">🤖</span>
                        AI Analysis
                        <span className="ml-2 text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-1 rounded-full">
                          Advanced
                        </span>
                      </h3>
                      <div className="space-y-3">
                        <div className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)]">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-[var(--text-sec)]">AI Consistency Score</span>
                            <span className={`text-sm font-bold ${getScoreColor(gradingResult.llmAnalysis.consistencyScore)}`}>
                              {Math.round(gradingResult.llmAnalysis.consistencyScore * 100)}%
                            </span>
                          </div>
                          <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${gradingResult.llmAnalysis.consistencyScore >= 0.8 ? 'bg-[var(--green)]' : gradingResult.llmAnalysis.consistencyScore >= 0.6 ? 'bg-[var(--mustard)]' : 'bg-[var(--coral)]'}`}
                              style={{ width: `${gradingResult.llmAnalysis.consistencyScore * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        {gradingResult.llmAnalysis.canonAlignment && (
                          <div className="p-3 bg-[var(--surface)] border border-[var(--rule)]">
                            <h4 className="text-xs font-medium text-[var(--text-sec)] mb-2">Canon Alignment</h4>
                            <div className="text-xs text-[var(--text-sec)] space-y-1">
                              {gradingResult.llmAnalysis.canonAlignment.matchedTerms.length > 0 && (
                                <div>
                                  <span className="font-medium">Matched:</span> {gradingResult.llmAnalysis.canonAlignment.matchedTerms.join(', ')}
                                </div>
                              )}
                              {gradingResult.llmAnalysis.canonAlignment.missingTerms.length > 0 && (
                                <div>
                                  <span className="font-medium">Missing:</span> {gradingResult.llmAnalysis.canonAlignment.missingTerms.join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Semantic Analysis */}
                  {gradingResult.semanticTerms && gradingResult.semanticTerms.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3">Semantic Analysis</h3>
                      <div className="space-y-3">
                        <div className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)]">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-[var(--text-sec)]">Semantic Score</span>
                            <span className={`text-sm font-bold ${getScoreColor(gradingResult.semanticScore)}`}>
                              {Math.round(gradingResult.semanticScore * 100)}%
                            </span>
                          </div>
                          <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${gradingResult.semanticScore >= 0.8 ? 'bg-[var(--green)]' : gradingResult.semanticScore >= 0.6 ? 'bg-[var(--mustard)]' : 'bg-[var(--coral)]'}`}
                              style={{ width: `${gradingResult.semanticScore * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="p-3 bg-[var(--surface)] border border-[var(--rule)]">
                          <h4 className="text-xs font-medium text-[var(--text-sec)] mb-2">Key Terms Identified</h4>
                          <div className="flex flex-wrap gap-2">
                            {gradingResult.semanticTerms.map((term, index) => (
                              <span key={index} className="px-2 py-1 text-xs bg-[var(--bg-raised)] border border-[var(--rule)] rounded-full">
                                {term.term} ({term.type})
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Usage Analysis */}
                  {gradingResult.usageAnalysis && gradingResult.usageAnalysis.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3">Term Usage Analysis</h3>
                      <div className="space-y-2">
                        {gradingResult.usageAnalysis.map((usage, index) => (
                          <div key={index} className={`p-3 border  ${
                            usage.isCorrectlyUsed.valid ? 'bg-[var(--green-dim)] border-[var(--green)]' : 'bg-[var(--mustard-dim)] border-[var(--mustard)]'
                          }`}>
                            <div className="flex items-start">
                              {usage.isCorrectlyUsed.valid ? (
                                <CheckCircle className="h-4 w-4 text-[var(--green)] mt-0.5 mr-2" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-[var(--mustard)] mt-0.5 mr-2" />
                              )}
                              <div>
                                <p className="text-sm font-medium">
                                  "{usage.term}" ({usage.type})
                                </p>
                                <p className="text-xs text-[var(--text-sec)] mt-1">
                                  {usage.isCorrectlyUsed.valid ? 'Correctly used' : usage.isCorrectlyUsed.reason}
                                </p>
                                {usage.suggestions && usage.suggestions.length > 0 && (
                                  <div className="mt-2">
                                    {usage.suggestions.map((suggestion, i) => (
                                      <p key={i} className="text-xs text-[var(--text-ter)]">{suggestion}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contradictions */}
                  {gradingResult.contradictions && gradingResult.contradictions.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3">Semantic Contradictions</h3>
                      <div className="space-y-2">
                        {gradingResult.contradictions.map((contradiction, index) => (
                          <div key={index} className="p-3 bg-[var(--coral-dim)] border border-[var(--coral)]">
                            <div className="flex items-start">
                              <XCircle className="h-4 w-4 text-[var(--coral)] mt-0.5 mr-2" />
                              <div>
                                <p className="text-sm text-[var(--coral)] font-medium">{contradiction.description}</p>
                                <p className="text-xs text-[var(--coral)] mt-1">{contradiction.suggestion}</p>
                                <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                                  contradiction.severity === 'high' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' : 
                                  contradiction.severity === 'medium' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 
                                  'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                                }`}>
                                  {contradiction.severity} severity
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggestions */}
                  {gradingResult.suggestions && gradingResult.suggestions.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3">Suggestions</h3>
                      <div className="space-y-2">
                        {gradingResult.suggestions.map((suggestion, index) => (
                          <div key={index} className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)]">
                            <div className="flex items-start">
                              <Lightbulb className="h-4 w-4 text-[var(--text-ter)] mt-0.5 mr-2" />
                              <div>
                                <p className="text-sm text-[var(--text-sec)]">{suggestion.suggestion || suggestion.reason}</p>
                                {suggestion.reasoning && (
                                  <p className="text-xs text-[var(--text-sec)] mt-1">{suggestion.reasoning}</p>
                                )}
                                {suggestion.priority && (
                                  <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                                    suggestion.priority === 'high' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' : 
                                    suggestion.priority === 'medium' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 
                                    'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                                  }`}>
                                    {suggestion.priority} priority
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Canon Sources */}
            <div className="bg-[var(--bg-raised)] shadow">
              <div className="px-6 py-4 border-b border-[var(--rule)]">
                <h3 className="text-lg font-semibold text-[var(--text-pri)]">Canon Sources</h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  <div className="flex items-center text-sm">
                    <BookOpen className="h-4 w-4 text-[var(--text-ter)] mr-2" />
                    <span>Assessment Framework</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <BookOpen className="h-4 w-4 text-[var(--text-ter)] mr-2" />
                    <span>Technical Architecture</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <BookOpen className="h-4 w-4 text-[var(--text-ter)] mr-2" />
                    <span>Knowledge Architecture</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <BookOpen className="h-4 w-4 text-[var(--text-ter)] mr-2" />
                    <span>Development Guide</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-[var(--bg-raised)] shadow">
              <div className="px-6 py-4 border-b border-[var(--rule)]">
                <h3 className="text-lg font-semibold text-[var(--text-pri)]">Quick Actions</h3>
              </div>
              <div className="p-6 space-y-3">
                <button className="w-full text-left px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]">
                  <Target className="h-4 w-4 inline mr-2" />
                  Check Glossary Terms
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]">
                  <FileText className="h-4 w-4 inline mr-2" />
                  View Full Report
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]">
                  <BookOpen className="h-4 w-4 inline mr-2" />
                  Browse Canon Docs
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};